import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import {
  CheckCircleIcon,
  BellIcon,
  EmojiHappyIcon,
  EmojiSadIcon,
  XCircleIcon,
} from '@heroicons/react/outline'
import toast from 'react-hot-toast'
import useDate from '../../hooks/useDate'
import useInteractionTypes from '../../hooks/useInteractionTypes'
import useTranslation from '../../hooks/useTranslation'

// Tailwind only generates classes it can see, so map the tab's bg colour to
// a ring colour with literal strings.
const RING_COLORS = {
  'bg-red-300': 'ring-red-300',
  'bg-green-300': 'ring-green-300',
  'bg-cyan-300': 'ring-cyan-300',
  'bg-blue-300': 'ring-blue-300',
}
const FLASH_MS = 1500

export default function Highlight({
  bgColor,
  textColor,
  interaction,
  highlight,
  onHighlightDone,
  isPositiveHighlight,
  showCompletionNotification,
  completionNotificationEnabled,
  isUpdatingCompletionNotification,
  completionNotificationError,
  completionNotificationUnavailable,
  onCompletionNotificationChanged,
}) {
  const [onHoverDone, setHoverDone] = useState(false)
  const [finishedHighlight, setFinishedHighlight] = useState(false)
  const { parse } = useDate()
  const { action, icon } = useInteractionTypes()
  const t = useTranslation()

  useEffect(() => {
    if (!finishedHighlight) {
      return
    }
    setTimeout(() => {
      onHighlightDone(highlight)
      setFinishedHighlight(false)
    }, 500)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishedHighlight])

  // Scroll the right-side timeline to the interaction this highlight came
  // from and flash it in the tab's colour so the eye lands on it.
  const jumpToInteraction = () => {
    const target = document.getElementById(
      `interaction-${interaction?.id || highlight.interaction_id}`,
    )

    if (!target) {
      return
    }

    target.scrollIntoView({ behavior: 'smooth', block: 'center' })

    const flash = [
      'rounded-lg',
      'ring-2',
      'ring-offset-2',
      RING_COLORS[bgColor] || 'ring-gray-300',
    ]
    target.classList.add(...flash)
    setTimeout(() => target.classList.remove(...flash), FLASH_MS)
  }

  // The done/undo icons live inside the clickable row.
  const finish = (event) => {
    event.stopPropagation()
    setFinishedHighlight(true)
  }

  const getIcon = () => {
    if (finishedHighlight) {
      return isPositiveHighlight ? (
        <EmojiSadIcon className="w-6 h-6 mr-1" />
      ) : (
        <EmojiHappyIcon className="w-6 h-6 mr-1" />
      )
    }

    if (onHoverDone) {
      return isPositiveHighlight ? (
        <XCircleIcon onClick={finish} className="w-6 h-6 mr-1 cursor-pointer" />
      ) : (
        <CheckCircleIcon
          onClick={finish}
          className={`w-6 h-6 mr-1 bg-${textColor
            .split('-')
            .slice(1, undefined)
            .join('-')} text-${bgColor
            .split('-')
            .slice(1, undefined)
            .join('-')} rounded-full cursor-pointer`}
        />
      )
    }

    return isPositiveHighlight ? (
      <EmojiHappyIcon className="w-6 h-6 mr-1" />
    ) : (
      <CheckCircleIcon className="w-6 h-6 mr-1" />
    )
  }

  const interactionIcon = interaction?.type ? icon(interaction.type) : null

  return (
    <div
      key={highlight.id}
      className={cx(
        'relative flex flex-wrap items-center justify-between gap-2 px-2 py-2 rounded-lg',
        bgColor,
        textColor,
        { 'todo-highlight-row': showCompletionNotification },
      )}>
      <button
        type="button"
        onClick={jumpToInteraction}
        className="flex items-center flex-1 min-w-0 text-left">
        <div
          onMouseOver={() => setHoverDone(true)}
          onMouseLeave={() => setHoverDone(false)}>
          {getIcon()}
        </div>
        <p
          className={cx('truncate', {
            'line-through': finishedHighlight,
          })}>
          {highlight.title}
        </p>
      </button>
      <div className="relative flex flex-shrink-0 items-center gap-3 ml-auto">
        <span className="todo-highlight-date flex items-center gap-1.5 text-sm font-medium whitespace-nowrap opacity-60 tabular-nums">
          {interactionIcon && (
            <span
              role="img"
              aria-label={action(interaction.type)}
              title={action(interaction.type)}
              className="flex-shrink-0">
              {React.cloneElement(interactionIcon, {
                className: 'flex-shrink-0 w-4 h-4',
              })}
            </span>
          )}
          {parse(
            interaction?.date || interaction?.created_at,
          ).toLocaleDateString()}
        </span>
        {showCompletionNotification && (
          <button
            type="button"
            aria-pressed={
              completionNotificationUnavailable
                ? 'mixed'
                : completionNotificationEnabled
            }
            aria-label={t(
              completionNotificationUnavailable
                ? 'customerModal.todoNotifications.unavailable'
                : completionNotificationError
                ? 'customerModal.todoNotifications.error'
                : completionNotificationEnabled
                ? 'customerModal.todoNotifications.subscribed'
                : 'customerModal.todoNotifications.subscribe',
            )}
            title={
              completionNotificationUnavailable
                ? t('customerModal.todoNotifications.unavailable')
                : completionNotificationError
                ? t('customerModal.todoNotifications.error')
                : t(
                    completionNotificationEnabled
                      ? 'customerModal.todoNotifications.unsubscribe'
                      : 'customerModal.todoNotifications.subscribe',
                  )
            }
            disabled={isUpdatingCompletionNotification}
            aria-disabled={completionNotificationUnavailable}
            onClick={(event) => {
              event.stopPropagation()

              if (completionNotificationUnavailable) {
                toast.error(t('customerModal.todoNotifications.unavailable'), {
                  id: 'todo-notification-unavailable',
                })

                return
              }

              onCompletionNotificationChanged(!completionNotificationEnabled)
            }}
            className={cx(
              'todo-notification-action relative inline-flex flex-shrink-0 items-center justify-center gap-1.5 w-7 h-7 -my-1.5 border rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
              {
                'text-red-800': !completionNotificationUnavailable,
                'focus-visible:ring-red-800':
                  !completionNotificationUnavailable,
                'focus-visible:ring-gray-500':
                  completionNotificationUnavailable,
                'border-red-800 bg-red-800 bg-opacity-10':
                  completionNotificationEnabled &&
                  !completionNotificationUnavailable,
                'border-red-800 border-opacity-30':
                  !completionNotificationEnabled &&
                  !completionNotificationError &&
                  !completionNotificationUnavailable,
                'border-red-800 ring-1 ring-red-800':
                  completionNotificationError &&
                  !completionNotificationUnavailable,
                'border-gray-400 bg-gray-100 text-gray-400':
                  completionNotificationUnavailable,
                'todo-notification-action--unavailable':
                  completionNotificationUnavailable,
                'opacity-50':
                  isUpdatingCompletionNotification &&
                  !completionNotificationUnavailable,
              },
            )}>
            <span
              aria-hidden="true"
              className="absolute top-1/2 left-1/2 w-12 h-12 -translate-x-1/2 -translate-y-1/2 sm:hidden"
            />
            <BellIcon
              className={cx(
                'flex-shrink-0 w-4 h-4',
                completionNotificationUnavailable
                  ? 'stroke-gray-400'
                  : 'stroke-red-800',
                {
                  'animate-pulse': isUpdatingCompletionNotification,
                  'opacity-60':
                    !completionNotificationEnabled &&
                    !completionNotificationUnavailable,
                },
              )}
            />
            <span className="todo-notification-label hidden">
              {t(
                completionNotificationUnavailable
                  ? 'customerModal.todoNotifications.unavailableShort'
                  : completionNotificationEnabled
                  ? 'customerModal.todoNotifications.unsubscribe'
                  : 'customerModal.todoNotifications.subscribe',
              )}
            </span>
          </button>
        )}
      </div>
    </div>
  )
}

Highlight.propTypes = {
  bgColor: PropTypes.string,
  textColor: PropTypes.string,
  highlight: PropTypes.shape({
    id: PropTypes.string,
    interaction_id: PropTypes.string,
    title: PropTypes.string,
    created_at: PropTypes.any,
  }),
  interaction: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    date: PropTypes.any,
    created_at: PropTypes.any,
  }),
  onHighlightDone: PropTypes.func,
  isPositiveHighlight: PropTypes.bool,
  showCompletionNotification: PropTypes.bool,
  completionNotificationEnabled: PropTypes.bool,
  isUpdatingCompletionNotification: PropTypes.bool,
  completionNotificationError: PropTypes.bool,
  completionNotificationUnavailable: PropTypes.bool,
  onCompletionNotificationChanged: PropTypes.func,
}
